import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { Company } from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { DialogContentTextOrMarkup } from '_lib/components/DialogContentText/DialogContentText';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { Stack } from '_lib/components/Stack/Stack';
import { Switch } from '_lib/components/Switch/Switch';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';

import { AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';
import { useOurSnackbar } from '_lib/utils/snackUtils';

export type DeactivateLastCompanyDialogProps = AsyncDialogProps & {
  accountId: string;
  company: Company;
};

const DeactivateLastCompanyDialog = (props: DeactivateLastCompanyDialogProps) => {
  const { accountId, company, onClose, ...dialogProps } = props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>([]);

  const [deactivateAccount, setDeactivateAccount] = useState<boolean>(false);

  const { enqueueSnackbar } = useOurSnackbar();

  const api = useContext(ApiContext);

  const companyDeactivateMutation = api.companies.useDelete();
  const accountDeactivateMutation = api.accounts.useDelete();

  const handleCancel = () => {
    onClose({ action: 'CANCEL' });
  };

  const handleConfirm = async () => {
    try {
      if (deactivateAccount) {
        await accountDeactivateMutation.deleteItem({ id: accountId });
        enqueueSnackbar('Account deactivated');
      } else {
        await companyDeactivateMutation.deleteItem({ id: company.id });
        enqueueSnackbar('Company deactivated');
      }

      onClose({ action: 'CONFIRM' });
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData.nonFieldErrors);
      }
    }
  };

  const isFetchingOrPending = companyDeactivateMutation.isPending || accountDeactivateMutation.isPending;

  return (
    <DialogWrapper onClose={onClose} titleText='Deactivate Company?' {...dialogProps}>
      <DialogContent sx={{ minWidth: '450px' }}>
        <BackdropSpinner open={isFetchingOrPending} />
        <Stack spacing={2.5} padding={2}>
          {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
          <DialogContentTextOrMarkup text='This is the last active company in the Account. Do you want to deactivate the Account as well?' />
          <FormControlLabel
            control={
              <Switch
                color='primary'
                checked={!!deactivateAccount || false}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setDeactivateAccount(checked);
                }}
              />
            }
            label={`Deactivate ${deactivateAccount ? 'Company and Account' : 'only Company'}`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogButton color='cancel' onClick={handleCancel} text='Cancel' />
        <DialogButton onClick={handleConfirm} text='Deactivate' disabled={isFetchingOrPending} />
      </DialogActions>
    </DialogWrapper>
  );
};

export const useDeactivateLastCompanyDialog = () => useAsyncDialog(DeactivateLastCompanyDialog);
